import React, { useState, useEffect, useCallback } from 'react';
import { Button, Table, Tag, message, Alert, Switch, Space } from 'antd';
import { useNavigate } from 'react-router-dom';
import { getBatchList, getBatchStatus, toggleBatchStatus, startBatch, stopBatch } from '../../services/api';
import styles from './BatchPage.module.css';

const BatchList = () => {
  const navigate = useNavigate();
  const [batches, setBatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [batchProcessingEnabled, setBatchProcessingEnabled] = useState(false);
  const [statusLoading, setStatusLoading] = useState(true);
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10, total: 0 });

  const fetchBatches = useCallback(async (page = 1, pageSize = 10) => {
    setLoading(true);
    setError(null);
    try {
      const response = await getBatchList({ page, limit: pageSize });
      const data = response.data;

      // Extract the array of batches from the paginated response
      const records = Array.isArray(data.data) ? data.data : 
                      Array.isArray(data.batches) ? data.batches :
                      Array.isArray(data.items) ? data.items :
                      Array.isArray(data) ? data : [];
      setBatches(records);

      // Update pagination state based on common API structures
      if (data.pagination) {
        setPagination({
          current: data.pagination.page,
          pageSize: data.pagination.limit,
          total: data.pagination.total,
        });
      } else if (data.total !== undefined) {
        setPagination({ current: data.page || page, pageSize: data.limit || pageSize, total: data.total });
      }
    } catch (err) {
      const errorMessage = err.response?.data?.detail || 'Failed to load batches.';
      setError(errorMessage);
      message.error(errorMessage);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchBatchStatus = useCallback(async () => {
    setStatusLoading(true);
    try {
      const response = await getBatchStatus();
      setBatchProcessingEnabled(response.data.enabled);
    } catch (err) {
      message.error('Failed to fetch Batch Payout Processing status.');
    } finally {
      setStatusLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchBatches();
    fetchBatchStatus();
  }, [fetchBatches, fetchBatchStatus]);

  const handleTableChange = (newPagination) => {
    fetchBatches(newPagination.current, newPagination.pageSize);
  };

  const handleStatusToggle = async (checked) => {
    setStatusLoading(true);
    try {
      await toggleBatchStatus({ enabled: checked });
      setBatchProcessingEnabled(checked);
      message.success(`Batch Payout Processing ${checked ? 'enabled' : 'disabled'}.`);
    } catch (err) {
      message.error('Failed to update Batch Payout Processing status.');
      setBatchProcessingEnabled(!checked); // Revert on error
    } finally {
      setStatusLoading(false);
    }
  };

  const handleBatchAction = async (batchId, action) => {
    try {
      if (action === 'start') {
        await startBatch(batchId);
        message.success(`Batch processing started for ID: ${batchId}.`);
      } else if (action === 'stop') {
        await stopBatch(batchId);
        message.success(`Batch processing stopped for ID: ${batchId}.`);
      }
      fetchBatches(pagination.current, pagination.pageSize); // Refresh current page
    } catch (err) {
      const errorMessage = err.response?.data?.detail || `Failed to ${action} batch.`;
      message.error(errorMessage);
    }
  };

  const getStatusTag = (status) => {
    switch (status) {
      case 'PENDING':
        return <Tag color="orange">PENDING</Tag>;
      case 'PROCESSING':
        return <Tag color="blue">PROCESSING</Tag>;
      case 'COMPLETED':
        return <Tag color="green">COMPLETED</Tag>;
      case 'FAILED':
        return <Tag color="red">FAILED</Tag>;
      default:
        return <Tag>{status}</Tag>;
    }
  };

  const columns = [
    {
      title: 'Batch Ref ID',
      dataIndex: 'batch_ref_id',
      key: 'batch_ref_id',
    },
    {
      title: 'Source Account',
      dataIndex: 'source_account_name',
      key: 'source_account_id',
      render: (text) => text || '—',
    },
    {
      title: 'Created Date',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (ts) => new Date(ts).toLocaleString(),
    },
    {
      title: 'Total Transactions',
      dataIndex: 'total_txns',
      key: 'total_txns',
    },
    {
      title: 'Total Amount',
      dataIndex: 'total_amount',
      key: 'total_amount',
      render: (amount) => new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR' }).format(amount),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: getStatusTag,
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (record) => {
        const batchId = record.id || record.batch_id;
        let actionButton = null;

        if (record.status === 'PENDING') {
          actionButton = (
            <Button
              style={{ backgroundColor: '#52C41A', borderColor: '#52C41A', color: 'white' }}
              onClick={() => handleBatchAction(batchId, 'start')}
            >
              Start
            </Button>
          );
        } else if (record.status === 'PROCESSING') {
          actionButton = (
            <Button danger onClick={() => handleBatchAction(batchId, 'stop')}>
              Stop
            </Button>
          );
        }
        return (
          <Space>
            {actionButton}
            <Button onClick={() => navigate(`/batch-payouts/${batchId}`)}>View Details</Button>
          </Space>
        );
      },
    },
  ];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2>Batch Payouts</h2>
        <Space>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span>Batch Payout Processing: </span>
            <Switch
              checked={batchProcessingEnabled}
              onChange={handleStatusToggle}
              loading={statusLoading}
            />
          </div>
          <Button type="primary" onClick={() => navigate('/batch-payouts/create')}>
            Create New Batch
          </Button>
        </Space>
      </div>
      {error && <Alert message="Error" description={error} type="error" showIcon closable />}
      <Table
        dataSource={batches}
        columns={columns}
        loading={loading}
        pagination={pagination}
        onChange={handleTableChange}
        rowKey={(record) => record.id || record.batch_id}
        className={styles.table}
      />
    </div>
  );
};
export default BatchList;